import { useState } from "react"
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  createColumnHelper,
  flexRender,
  type SortingState,
} from "@tanstack/react-table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Search, Download, Filter, FileText, Image, FileSpreadsheet } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { TableDateDisplay } from "@/components/ui/date-display"
import { formatDeviceType } from "@/lib/deviceType"

interface Device {
  id: string
  name: string
  serialNumber: string
  type: string | null
  leakAlarms: number
  lastUpdated: string
  customerId: string
  customerName: string
}

interface DevicesTableProps {
  devices: Device[]
  compact?: boolean
  onDeviceClick?: (deviceId: string) => void
}

const columnHelper = createColumnHelper<Device>()

export function DevicesTable({ devices, compact = false, onDeviceClick }: DevicesTableProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")

  const exportData = (format: string) => {
    let content: string
    let mimeType: string
    let filename: string

    switch (format) {
      case "csv": {
        const headers = "ID,Name,Serial Number,Type,Status,Last Updated,Leak Alarms"
        const rows = devices
          .map(
            (device) =>
              `${device.id},${device.name},${device.serialNumber},${formatDeviceType(device.type)},active,${device.lastUpdated},${device.leakAlarms}`,
          )
          .join("\n")
        content = headers + "\n" + rows
        mimeType = "text/csv;charset=utf-8;"
        filename = "devices-export.csv"
        break
      }

      case "pdf": {
        content = `%PDF-1.4
1 0 obj
<<
/Type /Catalog
/Pages 2 0 R
>>
endobj

2 0 obj
<<
/Type /Pages
/Kids [3 0 R]
/Count 1
>>
endobj

3 0 obj
<<
/Type /Page
/Parent 2 0 R
/MediaBox [0 0 612 792]
/Contents 4 0 R
>>
endobj

4 0 obj
<<
/Length 55
>>
stream
BT
/F1 12 Tf
100 700 Td
(Devices Export - ${devices.length} devices) Tj
ET
endstream
endobj

trailer
<<
/Size 5
/Root 1 0 R
>>
startxref
0
%%EOF`
        mimeType = "application/pdf"
        filename = "devices-export.pdf"
        break
      }

      case "png": {
        // Simple 1x1 transparent PNG
        content =
          "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
        mimeType = "image/png"
        filename = "devices-export.png"

        const link = document.createElement("a")
        link.href = `data:${mimeType};base64,${content}`
        link.download = filename
        link.click()
        return
      }

      default:
        content = JSON.stringify(devices, null, 2)
        mimeType = "application/json;charset=utf-8;"
        filename = "devices-export.json"
    }

    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
  }

  const columns = [
    columnHelper.accessor("name", {
      header: "Device",
      cell: (info) => (
        <div>
          <p className="font-medium">{info.getValue()}</p>
          <p className="text-xs text-muted-foreground">{info.row.original.serialNumber}</p>
        </div>
      ),
    }),
    columnHelper.accessor("type", {
      header: "Type",
      cell: (info) => (
        <Badge variant="outline" className="capitalize">
          {formatDeviceType(info.getValue())}
        </Badge>
      ),
    }),
    columnHelper.accessor("leakAlarms", {
      header: "Alarms",
      cell: (info) => (
        <Badge variant={info.getValue() > 0 ? "destructive" : "secondary"}>{info.getValue()}</Badge>
      ),
    }),
    columnHelper.accessor("lastUpdated", {
      header: "Last Updated",
      cell: (info) => (
        <span className="text-sm text-muted-foreground">
          <TableDateDisplay date={info.getValue()} />
        </span>
      ),
    }),
    ...(compact
      ? []
      : [
          columnHelper.display({
            id: "actions",
            header: "Actions",
            cell: ({ row }) => (
              <Button size="sm" variant="outline" onClick={() => onDeviceClick?.(row.original.id)}>
                View
              </Button>
            ),
          }),
        ]),
  ]

  const filteredData = devices.filter((device) => {
    if (statusFilter === "alerts") return device.leakAlarms > 0
    if (statusFilter === "normal") return device.leakAlarms === 0
    return true
  })

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: {
        pageSize: compact ? 5 : 10,
      },
    },
  })

  if (compact) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="text-xs">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="text-xs">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Devices Management</CardTitle>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Download className="size-4" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => exportData("csv")}>
                  <FileSpreadsheet className="size-4 mr-2" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportData("pdf")}>
                  <FileText className="size-4 mr-2" />
                  Export as PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportData("png")}>
                  <Image className="size-4 mr-2" />
                  Export as PNG
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                placeholder="Search devices..."
                value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
                onChange={(event) => table.getColumn("name")?.setFilterValue(event.target.value)}
                className="pl-8"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Filter className="size-4" />
                  Filter:{" "}
                  {statusFilter === "all"
                    ? "All"
                    : statusFilter === "alerts"
                      ? "With Alerts"
                      : "Normal"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setStatusFilter("all")}>
                  All Devices
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter("alerts")}>
                  With Alerts
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter("normal")}>
                  Normal Status
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between gap-x-2 py-4">
            <div className="text-sm text-muted-foreground">
              Showing {table.getRowModel().rows.length} of {filteredData.length} devices
            </div>
            <div className="flex gap-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
